import { useCallback, useState } from 'react';
import { IDataServices, ILoadLookupData, ILookupData, LookupData } from '../types';
import { IErrorHandling } from './withErrorHandling';
import { RiskAppPriorityService } from '../services';

export interface IUseDataContextProps {
    dataServices: IDataServices;
    lookupData: ILookupData;
    loadLookupData: ILoadLookupData;
}

export const useDataContext = (apiConnected: boolean, dataServices: IDataServices, { onError }: IErrorHandling): IUseDataContextProps => {
    const { app03ProjectTypeService, customFieldDfnService, benefitService, benefitTypeService, commitmentService, corporateRiskService, departmentalObjectivesService,
        dependencyService, directorateService, economicRingfenceService, entityStatusService, financialRiskService, fundingClassificationService,
        orgLevel1Service, keyWorkAreaService, measurementUnitService, metricService, milestoneService, milestoneTypeService, partnerOrganisationRiskMitigationActionService,
        partnerOrganisationRiskService, partnerOrganisationService, policyRingfenceService, projectBusinessCaseTypeService, projectPhaseService, projectStageService, reportingEntityTypeService,
        reportingFrequencyService, riskAppetiteService, riskDiscussionForumService, riskImpactLevelService, riskProbabilityService, riskTypeService, roleService,
        thresholdAppetiteService, thresholdService, budgetingEntitiesService, userGroupService, userPartnerOrganisationService,
        projectService, userDirectorateService, userProjectService, userService, workStreamService, ppVisionService, ppObjectiveService, portfolioService, orgLevel3Service,
        programmeService, programmeStageService, programmePhaseService, app03PortfolioService, app03TemplateService, app03ProjectService, app03SprintService, app03WorkflowService, app04TopicService, zCoreUserGroupService, cLComFrameworkService,
        riskAppPriorityService, riskAppSourceService, riskAppStageService, riskAppIncidentTypeService, riskAppReportingChannelService,
        riskAppLocationService, riskAppSeverityService, riskAppSystemService, riskAppStakeholderService, riskAppImpactTypeService, riskAppIssueService, riskAppIncidentService } = dataServices;
    const [lookupData, setLookupData] = useState(new LookupData());
    const logError = useCallback(onError, [onError]);


    const lld: ILoadLookupData = {
        app03ProjectTypes: useCallback(async forceReload => {
            if (apiConnected && (lookupData.App03ProjectTypes.length === 0 || forceReload)) {
                try {
                    const pt = await app03ProjectTypeService.readAllForLookup();
                    setLookupData(s => ({ ...s, App03ProjectTypes: pt }));
                } catch (err) { logError(`Error loading project types`, err); }
            }
        }, [apiConnected, lookupData.App03ProjectTypes.length, app03ProjectTypeService, logError]),
        riskImpactLevels: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskImpactLevels.length === 0 || forceReload)) {
                try {
                    const ril = await riskImpactLevelService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskImpactLevels: ril }));
                } catch (err) { logError(`Error loading risk impact levels`, err); }
            }
        }, [apiConnected, lookupData.RiskImpactLevels.length, riskImpactLevelService, logError]),
        customFieldDfns: useCallback(async forceReload => {
            if (apiConnected && (lookupData.CustomFieldDfns.length === 0 || forceReload)) {
                try {
                    const at = await customFieldDfnService.readAllForLookup();
                    setLookupData(s => ({ ...s, CustomFieldDfns: at }));
                } catch (err) { logError(`Error loading customField Dfns`, err); }
            }
        }, [apiConnected, lookupData.CustomFieldDfns.length, customFieldDfnService, logError]),
        benefits: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Benefits.length === 0 || forceReload)) {
                try {
                    const b = await benefitService.readAllForLookup();
                    setLookupData(s => ({ ...s, Benefits: b }));
                } catch (err) { logError(`Error loading benefits`, err); }
            }
        }, [apiConnected, lookupData.Benefits.length, benefitService, logError]),
        benefitTypes: useCallback(async forceReload => {
            if (apiConnected && (lookupData.BenefitTypes.length === 0 || forceReload)) {
                try {
                    const bt = await benefitTypeService.readAllForLookup();
                    setLookupData(s => ({ ...s, BenefitTypes: bt }));
                } catch (err) { logError(`Error loading benefit types`, err); }
            }
        }, [apiConnected, lookupData.BenefitTypes.length, benefitTypeService, logError]),
        commitments: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Commitments.length === 0 || forceReload)) {
                try {
                    const c = await commitmentService.readAllForLookup();
                    setLookupData(s => ({ ...s, Commitments: c }));
                } catch (err) { logError(`Error loading commitments`, err); }
            }
        }, [apiConnected, lookupData.Commitments.length, commitmentService, logError]),
        corporateRisks: useCallback(async forceReload => {
            if (apiConnected && (lookupData.CorporateRisks.length === 0 || forceReload)) {
                try {
                    const cr = await corporateRiskService.readAllForLookup();
                    setLookupData(s => ({ ...s, CorporateRisks: cr }));
                } catch (err) { logError(`Error loading corporate risks`, err); }
            }
        }, [apiConnected, lookupData.CorporateRisks.length, corporateRiskService, logError]),
        departmentalObjectives: useCallback(async forceReload => {
            if (apiConnected && (lookupData.DepartmentalObjectives.length === 0 || forceReload)) {
                try {
                    const d = await departmentalObjectivesService.readAllForLookup();
                    setLookupData(s => ({ ...s, DepartmentalObjectives: d }));
                } catch (err) { logError(`Error loading departmental objectives`, err); }
            }
        }, [apiConnected, lookupData.DepartmentalObjectives.length, departmentalObjectivesService, logError]),
        dependencies: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Dependencies.length === 0 || forceReload)) {
                try {
                    const d = await dependencyService.readAllForLookup();
                    setLookupData(s => ({ ...s, Dependencies: d }));
                } catch (err) { logError(`Error loading dependencies`, err); }
            }
        }, [apiConnected, lookupData.Dependencies.length, dependencyService, logError]),
        directorates: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Directorates.length === 0 || forceReload)) {
                try {
                    const d = await directorateService.readAllForLookup();
                    setLookupData(s => ({ ...s, Directorates: d }));
                } catch (err) { logError(`Error loading directorates`, err); }
            }
        }, [apiConnected, lookupData.Directorates.length, directorateService, logError]),
        economicRingfences: useCallback(async forceReload => {
            if (apiConnected && (lookupData.EconomicRingfences.length === 0 || forceReload)) {
                try {
                    const f = await economicRingfenceService.getItems();
                    setLookupData(s => ({ ...s, EconomicRingfences: f }));
                } catch (err) { logError(`Error loading economic ring-fences`, err); }
            }
        }, [apiConnected, lookupData.EconomicRingfences.length, economicRingfenceService, logError]),
        entityStatuses: useCallback(async forceReload => {
            if (apiConnected && (lookupData.EntityStatuses.length === 0 || forceReload)) {
                try {
                    const e = await entityStatusService.readAllForLookup();
                    setLookupData(s => ({ ...s, EntityStatuses: e }));
                } catch (err) { logError(`Error loading entity statuses`, err); }
            }
        }, [apiConnected, lookupData.EntityStatuses.length, entityStatusService, logError]),
        financialRisks: useCallback(async forceReload => {
            if (apiConnected && (lookupData.FinancialRisks.length === 0 || forceReload)) {
                try {
                    const r = await financialRiskService.readAllForLookup();
                    setLookupData(s => ({ ...s, FinancialRisks: r }));
                } catch (err) { logError(`Error loading financial risks`, err); }
            }
        }, [apiConnected, lookupData.FinancialRisks.length, financialRiskService, logError]),
        fundingClassifications: useCallback(async forceReload => {
            if (apiConnected && (lookupData.FundingClassifications.length === 0 || forceReload)) {
                try {
                    const f = await fundingClassificationService.getItems();
                    setLookupData(s => ({ ...s, FundingClassifications: f }));
                } catch (err) { logError(`Error loading funding classifications`, err); }
            }
        }, [apiConnected, lookupData.FundingClassifications.length, fundingClassificationService, logError]),
        groups: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Groups.length === 0 || forceReload)) {
                try {
                    const g = await orgLevel1Service.readAllForLookup();
                    setLookupData(s => ({ ...s, Groups: g }));
                } catch (err) { logError(`Error loading groups`, err); }
            }
        }, [apiConnected, lookupData.Groups.length, orgLevel1Service, logError]),
        keyWorkAreas: useCallback(async forceReload => {
            if (apiConnected && (lookupData.KeyWorkAreas.length === 0 || forceReload)) {
                try {
                    const kwa = await keyWorkAreaService.readAllForLookup();
                    setLookupData(s => ({ ...s, KeyWorkAreas: kwa }));
                } catch (err) { logError(`Error loading key work areas`, err); }
            }
        }, [apiConnected, lookupData.KeyWorkAreas.length, keyWorkAreaService, logError]),
        measurementUnits: useCallback(async forceReload => {
            if (apiConnected && (lookupData.MeasurementUnits.length === 0 || forceReload)) {
                try {
                    const mu = await measurementUnitService.readAllForLookup();
                    setLookupData(s => ({ ...s, MeasurementUnits: mu }));
                } catch (err) { logError(`Error loading measurement units`, err); }
            }
        }, [apiConnected, lookupData.MeasurementUnits.length, measurementUnitService, logError]),
        metrics: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Metrics.length === 0 || forceReload)) {
                try {
                    const m = await metricService.readAllForLookup();
                    setLookupData(s => ({ ...s, Metrics: m }));
                } catch (err) { logError(`Error loading metrics`, err); }
            }
        }, [apiConnected, lookupData.Metrics.length, metricService, logError]),
        milestones: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Milestones.length === 0 || forceReload)) {
                try {
                    const m = await milestoneService.readAllForLookup();
                    setLookupData(s => ({ ...s, Milestones: m }));
                } catch (err) { logError(`Error loading milestones`, err); }
            }
        }, [apiConnected, lookupData.Milestones.length, milestoneService, logError]),
        milestoneTypes: useCallback(async forceReload => {
            if (apiConnected && (lookupData.MilestoneTypes.length === 0 || forceReload)) {
                try {
                    const mt = await milestoneTypeService.readAllForLookup();
                    setLookupData(s => ({ ...s, MilestoneTypes: mt }));
                } catch (err) { logError(`Error loading milestone types`, err); }
            }
        }, [apiConnected, lookupData.MilestoneTypes.length, milestoneTypeService, logError]),
        partnerOrganisationRiskMitigationActions: useCallback(async forceReload => {
            if (apiConnected && (lookupData.PartnerOrganisationRiskMitigationActions.length === 0 || forceReload)) {
                try {
                    const porma = await partnerOrganisationRiskMitigationActionService.readAllForLookup();
                    setLookupData(s => ({ ...s, PartnerOrganisationRiskMitigationActions: porma }));
                } catch (err) { logError(`Error loading partner organisation risk mitigating actions`, err); }
            }
        }, [apiConnected, lookupData.PartnerOrganisationRiskMitigationActions.length, partnerOrganisationRiskMitigationActionService, logError]),
        partnerOrganisationRisks: useCallback(async forceReload => {
            if (apiConnected && (lookupData.PartnerOrganisationRisks.length === 0 || forceReload)) {
                try {
                    const por = await partnerOrganisationRiskService.readAllForLookup();
                    setLookupData(s => ({ ...s, PartnerOrganisationRisks: por }));
                } catch (err) { logError(`Error loading partner organisation risks`, err); }
            }
        }, [apiConnected, lookupData.PartnerOrganisationRisks.length, partnerOrganisationRiskService, logError]),
        partnerOrganisations: useCallback(async forceReload => {
            if (apiConnected && (lookupData.PartnerOrganisations.length === 0 || forceReload)) {
                try {
                    const po = await partnerOrganisationService.readAllForLookup();
                    setLookupData(s => ({ ...s, PartnerOrganisations: po }));
                } catch (err) { logError(`Error loading partner organisations`, err); }
            }
        }, [apiConnected, lookupData.PartnerOrganisations.length, partnerOrganisationService, logError]),
        policyRingfences: useCallback(async forceReload => {
            if (apiConnected && (lookupData.PolicyRingfences.length === 0 || forceReload)) {
                try {
                    const p = await policyRingfenceService.getItems();
                    setLookupData(s => ({ ...s, PolicyRingfences: p }));
                } catch (err) { logError(`Error loading policy ring-fences`, err); }
            }
        }, [apiConnected, lookupData.PolicyRingfences.length, policyRingfenceService, logError]),
        projectPhases: useCallback(async forceReload => {
            if (apiConnected && (lookupData.ProjectPhases.length === 0 || forceReload)) {
                try {
                    const pp = await projectPhaseService.readAllForLookup();
                    setLookupData(s => ({ ...s, ProjectPhases: pp }));
                } catch (err) { logError(`Error loading project phases`, err); }
            }
        }, [apiConnected, lookupData.ProjectPhases.length, projectPhaseService, logError]),
        projectStages: useCallback(async forceReload => {
            if (apiConnected && (lookupData.ProjectStages.length === 0 || forceReload)) {
                try {
                    const ps = await projectStageService.readAllForLookup();
                    setLookupData(s => ({ ...s, ProjectStages: ps }));
                } catch (err) { logError(`Error loading project stages`, err); }
            }
        }, [apiConnected, lookupData.ProjectStages.length, projectStageService, logError]),
        projectBusinessCaseTypes: useCallback(async forceReload => {
            if (apiConnected && (lookupData.ProjectBusinessCaseTypes.length === 0 || forceReload)) {
                try {
                    const pbct = await projectBusinessCaseTypeService.readAllForLookup();
                    setLookupData(s => ({ ...s, ProjectBusinessCaseTypes: pbct }));
                } catch (err) { logError(`Error loading project business case types`, err); }
            }
        }, [apiConnected, lookupData.ProjectBusinessCaseTypes.length, projectBusinessCaseTypeService, logError]),
        projects: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Projects.length === 0 || forceReload)) {
                try {
                    const e = await projectService.readAllForLookup();
                    setLookupData(s => ({ ...s, Projects: e }));
                } catch (err) { logError(`Error loading projects`, err); }
            }
        }, [apiConnected, lookupData.Projects.length, projectService, logError]),
        reportingEntityTypes: useCallback(async forceReload => {
            if (apiConnected && (lookupData.ReportingEntityTypes.length === 0 || forceReload)) {
                try {
                    const r = await reportingEntityTypeService.readAllForLookup();
                    setLookupData(s => ({ ...s, ReportingEntityTypes: r }));
                } catch (err) { logError(`Error loading reporting entity types`, err); }
            }
        }, [apiConnected, lookupData.ReportingEntityTypes.length, reportingEntityTypeService, logError]),
        reportingFrequencies: useCallback(async forceReload => {
            if (apiConnected && (lookupData.ReportingFrequencies.length === 0 || forceReload)) {
                try {
                    const r = await reportingFrequencyService.readAllForLookup();
                    setLookupData(s => ({ ...s, ReportingFrequencies: r }));
                } catch (err) { logError(`Error loading reporting frequencies`, err); }
            }
        }, [apiConnected, lookupData.ReportingFrequencies.length, reportingFrequencyService, logError]),
        riskAppetites: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppetites.length === 0 || forceReload)) {
                try {
                    const ra = await riskAppetiteService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppetites: ra }));
                } catch (err) { logError(`Error loading risk appetites`, err); }
            }
        }, [apiConnected, lookupData.RiskAppetites.length, riskAppetiteService, logError]),
        riskDiscussionForums: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskDiscussionForums.length === 0 || forceReload)) {
                try {
                    const ra = await riskDiscussionForumService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskDiscussionForums: ra }));
                } catch (err) { logError(`Error loading risk discussion forums`, err); }
            }
        }, [apiConnected, lookupData.RiskDiscussionForums.length, riskDiscussionForumService, logError]),


        riskProbabilities: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskProbabilities.length === 0 || forceReload)) {
                try {
                    const rp = await riskProbabilityService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskProbabilities: rp }));
                } catch (err) { logError(`Error loading risk probabilities`, err); }
            }
        }, [apiConnected, lookupData.RiskProbabilities.length, riskProbabilityService, logError]),
        riskTypes: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskTypes.length === 0 || forceReload)) {
                try {
                    const rt = await riskTypeService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskTypes: rt }));
                } catch (err) { logError(`Error loading risk types`, err); }
            }
        }, [apiConnected, lookupData.RiskTypes.length, riskTypeService, logError]),
        roles: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Roles.length === 0 || forceReload)) {
                try {
                    const r = await roleService.readAllForLookup();
                    setLookupData(s => ({ ...s, Roles: r }));
                } catch (err) { logError(`Error loading roles`, err); }
            }
        }, [apiConnected, lookupData.Roles.length, roleService, logError]),
        thresholdAppetites: useCallback(async forceReload => {
            if (apiConnected && (lookupData.ThresholdAppetites.length === 0 || forceReload)) {
                try {
                    const ta = await thresholdAppetiteService.readAllForLookup();
                    setLookupData(s => ({ ...s, ThresholdAppetites: ta }));
                } catch (err) { logError(`Error loading threshold appetites`, err); }
            }
        }, [apiConnected, lookupData.ThresholdAppetites.length, thresholdAppetiteService, logError]),
        thresholds: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Thresholds.length === 0 || forceReload)) {
                try {
                    const t = await thresholdService.readAllForLookup();
                    setLookupData(s => ({ ...s, Thresholds: t }));
                } catch (err) { logError(`Error loading thresholds`, err); }
            }
        }, [apiConnected, lookupData.Thresholds.length, thresholdService, logError]),
        budgetingEntities: useCallback(async forceReload => {
            if (apiConnected && (lookupData.BudgetingEntities.length === 0 || forceReload)) {
                try {
                    const u = await budgetingEntitiesService.getItems();
                    setLookupData(s => ({ ...s, BudgetingEntities: u }));
                } catch (err) { logError(`Error loading budgeting entities`, err); }
            }
        }, [apiConnected, lookupData.BudgetingEntities.length, budgetingEntitiesService, logError]),
        users: {
            all: useCallback(async forceReload => {
                if (apiConnected && (lookupData.Users.All.length === 0 || forceReload)) {
                    try {
                        const u = await userService.readAllForLookup(true); //changed readAllForLookup to include CustomerID
                        setLookupData(s => ({ ...s, Users: { ...s.Users, All: u } }));
                    } catch (err) { logError(`Error loading all users`, err); }
                }
            }, [apiConnected, lookupData.Users.All.length, userService, logError]),
            enabled: useCallback(async forceReload => {
                if (apiConnected && (lookupData.Users.Enabled.length === 0 || forceReload)) {
                    try {
                        const u = await userService.readAllForLookup(false);
                        setLookupData(s => ({ ...s, Users: { ...s.Users, Enabled: u } }));
                    } catch (err) { logError(`Error loading enabled users`, err); }
                }
            }, [apiConnected, lookupData.Users.Enabled.length, userService, logError])
        },
        userDirectorates: useCallback(async forceReload => {
            if (apiConnected && (lookupData.UserDirectorates.length === 0 || forceReload)) {
                try {
                    const ud = await userDirectorateService.readAllForLookup();
                    setLookupData(s => ({ ...s, UserDirectorates: ud }));
                } catch (err) { logError(`Error loading user directorates`, err); }
            }
        }, [apiConnected, lookupData.UserDirectorates.length, userDirectorateService, logError]),
        userGroups: useCallback(async forceReload => {
            if (apiConnected && (lookupData.UserGroups.length === 0 || forceReload)) {
                try {
                    const u = await userGroupService.readAllForLookup();
                    setLookupData(s => ({ ...s, UserGroups: u }));
                } catch (err) { logError(`Error loading user groups`, err); }
            }
        }, [apiConnected, lookupData.UserGroups.length, userGroupService, logError]),
        userPartnerOrganisations: useCallback(async forceReload => {
            if (apiConnected && (lookupData.UserPartnerOrganisations.length === 0 || forceReload)) {
                try {
                    const u = await userPartnerOrganisationService.readAllForLookup();
                    setLookupData(s => ({ ...s, UserPartnerOrganisations: u }));
                } catch (err) { logError(`Error loading user partner organisations`, err); }
            }
        }, [apiConnected, lookupData.UserPartnerOrganisations.length, userPartnerOrganisationService, logError]),
        userProjects: useCallback(async forceReload => {
            if (apiConnected && (lookupData.UserProjects.length === 0 || forceReload)) {
                try {
                    const up = await userProjectService.readAllForLookup();
                    setLookupData(s => ({ ...s, UserProjects: up }));
                } catch (err) { logError(`Error loading user projects`, err); }
            }
        }, [apiConnected, lookupData.UserProjects.length, userProjectService, logError]),
        workStreams: useCallback(async forceReload => {
            if (apiConnected && (lookupData.WorkStreams.length === 0 || forceReload)) {
                try {
                    const ws = await workStreamService.readAllForLookup();
                    setLookupData(s => ({ ...s, WorkStreams: ws }));
                } catch (err) { logError(`Error loading work streams`, err); }
            }
        }, [apiConnected, lookupData.WorkStreams.length, workStreamService, logError]),
        ppVisions: useCallback(async forceReload => {
            if (apiConnected && (lookupData.PPVisions.length === 0 || forceReload)) {
                try {
                    const v = await ppVisionService.readAllForLookup();
                    setLookupData(s => ({ ...s, PPVisions: v }));
                } catch (err) { logError(`Error loading visions`, err); }
            }
        }, [apiConnected, lookupData.PPVisions.length, ppVisionService, logError]),
        ppObjectives: useCallback(async forceReload => {
            if (apiConnected && (lookupData.PPObjectives.length === 0 || forceReload)) {
                try {
                    const o = await ppObjectiveService.readAllForLookup();
                    setLookupData(s => ({ ...s, PPObjectives: o }));
                } catch (err) { logError(`Error loading objectives`, err); }
            }
        }, [apiConnected, lookupData.PPObjectives.length, ppObjectiveService, logError]),
        portfolios: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Portfolios.length === 0 || forceReload)) {
                try {
                    const e = await portfolioService.readAllForLookup();
                    setLookupData(s => ({ ...s, Portfolios: e }));
                } catch (err) { logError(`Error loading portfolios`, err); }
            }
        }, [apiConnected, lookupData.Portfolios.length, portfolioService, logError]),
        orgLevel3s: useCallback(async forceReload => {
            if (apiConnected && (lookupData.OrgLevel3s.length === 0 || forceReload)) {
                try {
                    const e = await orgLevel3Service.readAllForLookup();
                    setLookupData(s => ({ ...s, OrgLevel3s: e }));
                } catch (err) { logError(`Error loading orgLevel3s`, err); }
            }
        }, [apiConnected, lookupData.OrgLevel3s.length, orgLevel3Service, logError]),
        programmes: useCallback(async forceReload => {
            if (apiConnected && (lookupData.Programmes.length === 0 || forceReload)) {
                try {
                    const e = await programmeService.readAllForLookup();
                    setLookupData(s => ({ ...s, Programmes: e }));
                } catch (err) { logError(`Error loading programmes`, err); }
            }
        }, [apiConnected, lookupData.Programmes.length, programmeService, logError]),
        programmeStages: useCallback(async forceReload => {
            if (apiConnected && (lookupData.ProgrammeStages.length === 0 || forceReload)) {
                try {
                    const e = await programmeStageService.readAllForLookup();
                    setLookupData(s => ({ ...s, ProgrammeStages: e }));
                } catch (err) { logError(`Error loading programme stages`, err); }
            }
        }, [apiConnected, lookupData.ProgrammeStages.length, programmeStageService, logError]),
        programmePhases: useCallback(async forceReload => {
            if (apiConnected && (lookupData.ProgrammePhases.length === 0 || forceReload)) {
                try {
                    const pp = await programmePhaseService.readAllForLookup();
                    setLookupData(s => ({ ...s, ProgrammePhases: pp }));
                } catch (err) { logError(`Error loading programme phases`, err); }
            }
        }, [apiConnected, lookupData.ProgrammePhases.length, programmePhaseService, logError]),
        app03Portfolios: useCallback(async forceReload => {
            if (apiConnected && (lookupData.App03Portfolios.length === 0 || forceReload)) {
                try {
                    const p = await app03PortfolioService.readAllForLookup();
                    setLookupData(s => ({ ...s, App03Portfolios: p }));
                } catch (err) { logError(`Error loading app03Portfolios`, err); }
            }
        }, [apiConnected, lookupData.App03Portfolios.length, app03PortfolioService, logError]),

        app03Templates: useCallback(async forceReload => {
            if (apiConnected && (lookupData.App03Templates.length === 0 || forceReload)) {
                try {
                    const templates = await app03TemplateService.readAllForLookup();
                    setLookupData(s => ({ ...s, App03Templates: templates }));
                } catch (err) { logError(`Error loading app03Templates`, err); }
            }
        }, [apiConnected, lookupData.App03Templates.length, app03TemplateService, logError]),

        app03Projects: useCallback(async forceReload => {
            if (apiConnected && (lookupData.App03Projects.length === 0 || forceReload)) {
                try {
                    const p = await app03ProjectService.readAllForLookup();
                    setLookupData(s => ({ ...s, App03Projects: p }));
                } catch (err) { logError(`Error loading app03Projects`, err); }
            }
        }, [apiConnected, lookupData.App03Projects.length, app03ProjectService, logError]),

        app03Sprints: useCallback(async forceReload => {
            if (apiConnected && (lookupData.App03Sprints.length === 0 || forceReload)) {
                try {
                    const sprints = await app03SprintService.readAllForLookup();
                    setLookupData(s => ({ ...s, App03Sprints: sprints }));
                } catch (err) { logError(`Error loading app03Sprints`, err); }
            }
        }, [apiConnected, lookupData.App03Sprints.length, app03SprintService, logError]),

        app03Workflows: useCallback(async forceReload => {
            if (apiConnected && (lookupData.App03Workflows.length === 0 || forceReload)) {
                try {
                    const wf = await app03WorkflowService.readAllForLookup();
                    setLookupData(s => ({ ...s, App03Workflows: wf }));
                } catch (err) { logError(`Error loading app03workfolows`, err); }
            }
        }, [apiConnected, lookupData.App03Workflows.length, app03WorkflowService, logError]),

        app04Topics: useCallback(async forceReload => {
            if (apiConnected && (lookupData.App04Topics.length === 0 || forceReload)) {
                try {
                    const topics = await app04TopicService.readAllForLookup();
                    setLookupData(s => ({ ...s, App04Topics: topics }));
                } catch (err) { logError(`Error loading app04Topics`, err); }
            }
        }, [apiConnected, lookupData.App04Topics.length, app04TopicService, logError]),

        zCoreUserGroups: useCallback(async forceReload => {
            if (apiConnected && (lookupData.ZCoreUserGroups.length === 0 || forceReload)) {
                try {
                    const ug = await zCoreUserGroupService.readAllForLookup();
                    setLookupData(s => ({ ...s, ZCoreUserGroups: ug }));
                } catch (err) { logError(`Error loading zedx user groups`, err); }
            }
        }, [apiConnected, lookupData.ZCoreUserGroups.length, zCoreUserGroupService, logError]),

        cLComFrameworks: useCallback(async forceReload => {
            if (apiConnected && (lookupData.CLComFrameworks.length === 0 || forceReload)) {
                try {
                    const cf = await cLComFrameworkService.readAllForLookup();
                    setLookupData(s => ({ ...s, CLComFrameworks: cf }));
                } catch (err) { logError(`Error loading CLComFrameworks`, err); }
            }
        }, [apiConnected, lookupData.CLComFrameworks.length, cLComFrameworkService, logError]),

        riskAppPriorities: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppPriorities.length === 0 || forceReload)) {
                try {
                    const p = await riskAppPriorityService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppPriorities: p }));
                } catch (err) { logError(`Error loading RiskAppPriorites`, err); }
            }
        }, [apiConnected, lookupData.RiskAppPriorities.length, riskAppPriorityService, logError]),

        riskAppSources: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppSources.length === 0 || forceReload)) {
                try {
                    const src = await riskAppSourceService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppSources: src }));
                } catch (err) { logError(`Error loading RiskAppSources`, err); }
            }
        }, [apiConnected, lookupData.RiskAppSources.length, riskAppSourceService, logError]),

        riskAppStages: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppStages.length === 0 || forceReload)) {
                try {
                    const st = await riskAppStageService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppStages: st }));
                } catch (err) { logError(`Error loading RiskAppStages`, err); }
            }
        }, [apiConnected, lookupData.RiskAppStages.length, riskAppStageService, logError]),

        riskAppIncidentTypes: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppIncidentTypes.length === 0 || forceReload)) {
                try {
                    const t = await riskAppIncidentTypeService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppIncidentTypes: t }));
                } catch (err) { logError(`Error loading RiskAppIncidentTypes`, err); }
            }
        }, [apiConnected, lookupData.RiskAppIncidentTypes.length, riskAppIncidentTypeService, logError]),

        riskAppReportingChannels: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppReportingChannels.length === 0 || forceReload)) {
                try {
                    const c = await riskAppReportingChannelService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppReportingChannels: c }));
                } catch (err) { logError(`Error loading RiskAppReportingChannels`, err); }
            }
        }, [apiConnected, lookupData.RiskAppReportingChannels.length, riskAppReportingChannelService, logError]),

        riskAppLocations: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppLocations.length === 0 || forceReload)) {
                try {
                    const loc = await riskAppLocationService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppLocations: loc }));
                } catch (err) { logError(`Error loading RiskAppLocations`, err); }
            }
        }, [apiConnected, lookupData.RiskAppLocations.length, riskAppLocationService, logError]),

        riskAppSeverities: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppSeverities.length === 0 || forceReload)) {
                try {
                    const sev = await riskAppSeverityService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppSeverities: sev }));
                } catch (err) { logError(`Error loading RiskAppSeverities`, err); }
            }
        }, [apiConnected, lookupData.RiskAppSeverities.length, riskAppSeverityService, logError]),

        riskAppSystems: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppSystems.length === 0 || forceReload)) {
                try {
                    const data = await riskAppSystemService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppSystems: data }));
                } catch (err) { logError(`Error loading RiskAppSystems`, err); }
            }
        }, [apiConnected, lookupData.RiskAppSystems.length, riskAppSystemService, logError]),

        riskAppStakeholders: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppStakeholders.length === 0 || forceReload)) {
                try {
                    const data = await riskAppStakeholderService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppStakeholders: data }));
                } catch (err) { logError(`Error loading RiskAppStakeholders`, err); }
            }
        }, [apiConnected, lookupData.RiskAppStakeholders.length, riskAppStakeholderService, logError]),

        riskAppImpactTypes: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppImpactTypes.length === 0 || forceReload)) {
                try {
                    const data = await riskAppImpactTypeService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppImpactTypes: data }));
                } catch (err) { logError(`Error loading RiskAppImpactTypes`, err); }
            }
        }, [apiConnected, lookupData.RiskAppImpactTypes.length, riskAppImpactTypeService, logError]),

        riskAppIssues: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppIssues.length === 0 || forceReload)) {
                try {
                    const data = await riskAppIssueService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppIssues: data }));
                } catch (err) { logError(`Error loading RiskAppIssues`, err); }
            }
        }, [apiConnected, lookupData.RiskAppIssues.length, riskAppIssueService, logError]),

        riskAppIncidents: useCallback(async forceReload => {
            if (apiConnected && (lookupData.RiskAppIncidents.length === 0 || forceReload)) {
                try {
                    const data = await riskAppIncidentService.readAllForLookup();
                    setLookupData(s => ({ ...s, RiskAppIncidents: data }));
                } catch (err) { logError(`Error loading RiskAppIncidents`, err); }
            }
        }, [apiConnected, lookupData.RiskAppIncidents.length, riskAppIncidentService, logError]),

    };

    return { dataServices: dataServices, lookupData: lookupData, loadLookupData: lld };
};
